<template>
  <c-modal :is-open="showProductDialog" :on-close="cancel" size="xl">
    <form @submit.prevent="submit">
      <c-modal-content ref="content" borderRadius="base" maxW="1000px" :p="{ sm: '1.25rem' }">
        <c-modal-header pb="0">
          <c-text as="span" font-size="md" font-weight="bold">
            {{ $t("productReturnV2.common.productToReturn") }}
            <c-text as="span" color="brand.greenDark">*</c-text>
          </c-text>
        </c-modal-header>
        <c-modal-close-button />
        <c-modal-body>
          <c-box>
            <c-box>
              <c-box mb="lg">
                <c-flex
                  v-if="form.sku && form.name"
                  border-radius="input"
                  border="1px solid"
                  border-color="inherit"
                  px="4"
                  height="12"
                  font-size="1rem !important"
                  width="100%"
                  justify-content="space-between"
                  align-items="center"
                >
                  <c-text as="span" class="name">{{ form.sku }} {{ form.name }}</c-text>
                  <button class="delete" @click="deleteProduct()">
                    <c-icon name="trashcan" size="1.5rem"></c-icon>
                  </button>
                </c-flex>
                <QuickProductReturnSearch
                  v-else
                  :input-class="`form-control ${
                    getFieldClasses('sku')['is-invalid'] ? 'is-invalid' : ''
                  }`"
                  :set-picked-product-to-field="true"
                  :placeholder="$t('productReturnV2.productEditor.skuFieldSearch')"
                  :include-archived="true"
                  @field-blur="onFieldBlur('sku')"
                  @field-change="onFieldChange('sku')"
                  @product-picked="productPickedFromQuickSearch"
                >
                  <c-box v-if="isErrorField('sku')" slot="after-input" class="invalid-feedback">
                    {{ $t("productReturnV2.productEditor.skuFieldInvalid") }}
                  </c-box>
                </QuickProductReturnSearch>
              </c-box>
            </c-box>

            <c-box class="products_editor__content">
              <c-form-control
                v-if="proFormaRequired"
                is-required
                :is-invalid="inputIsInvalid('tariffCode')"
              >
                <c-form-label for="category" font-weight="bold" mt="sm" mb="xs">
                  {{ $t("productReturn.common.customsInformation.categoryTitle") }}
                </c-form-label>
                <c-select
                  id="category"
                  size="lg"
                  border-radius="input"
                  v-model="form.tariffCode"
                  @blur="onFieldBlur('tariffCode')"
                  @change="onFieldChange('tariffCode')"
                  data-cy="cr-product-tariff-code"
                >
                  <option v-for="type in categoryTypes" :key="type.value" :value="type.value">
                    {{ $t(type.label) }}
                  </option>
                </c-select>
                <c-box v-if="isErrorField('tariffCode')" color="brand.red" font-size="sm" my="xs">
                  {{
                    $t("common.error.fieldInvalid", {
                      field: $t("productReturn.common.customsInformation.categoryTitle"),
                    })
                  }}
                </c-box>
              </c-form-control>

              <c-form-control is-required :is-invalid="inputIsInvalid('returnReason')">
                <c-form-label for="returnReason" font-weight="bold" mt="sm" mb="xs">
                  {{ $t("productReturn.productEditor.returnReason") }}
                </c-form-label>

                <c-select
                  id="returnReason"
                  size="lg"
                  border-radius="input"
                  :max-width="{ md: '50%' }"
                  v-model="form.returnReason"
                  @blur="onFieldBlur('returnReason')"
                  data-cy="cr-product-return-reason"
                >
                  <option v-for="type in returnReasonTypes" :key="type.value" :value="type.value">
                    {{ $t(type.label) }}
                  </option>
                </c-select>
                <c-box v-if="isErrorField('returnReason')" color="brand.red" font-size="sm" my="xs">
                  {{ $t("productReturn.productEditor.returnReason") }}
                </c-box>
              </c-form-control>

              <c-form-control is-required :is-invalid="inputIsInvalid('problemOccurence')">
                <c-form-label for="problemOccurence" font-weight="bold" mt="sm" mb="xs">
                  {{ $t("productReturn.productEditor.problemOccurence") }}
                </c-form-label>

                <c-select
                  id="problemOccurence"
                  size="lg"
                  border-radius="input"
                  :max-width="{ md: '50%' }"
                  v-model="form.problemOccurence"
                  @blur="onFieldBlur('problemOccurence')"
                  data-cy="cr-product-problem-occurence"
                >
                  <option
                    v-for="type in problemOccurenceTypes"
                    :key="type.value"
                    :value="type.value"
                  >
                    {{ $t(type.label) }}
                  </option>
                </c-select>
                <c-box
                  v-if="isErrorField('problemOccurence')"
                  color="brand.red"
                  font-size="sm"
                  my="xs"
                >
                  {{ $t("productReturn.productEditor.problemOccurence") }}
                </c-box>
              </c-form-control>

              <c-form-control :is-invalid="inputIsInvalid('additionalInformation')">
                <c-form-label for="additionalInformation" font-weight="bold" mt="sm" mb="xs">
                  {{ $t("productReturn.productEditor.additionalInformation") }}
                </c-form-label>

                <c-textarea
                  id="additionalInformation"
                  v-model.trim="form.additionalInformation"
                  :maxlength="$v.form['additionalInformation'].$params.maxLength.max"
                  type="additionalInformation"
                  @blur="onFieldBlur('additionalInformation')"
                  data-cy="cr-product-additional-information"
                />
                <small v-chakra d="block" mt="xs">
                  {{
                    $tc("common.form.charactersLeft", getCharactersLeft("additionalInformation"), {
                      charCount: getCharactersLeft("additionalInformation"),
                    })
                  }}
                </small>
                <c-box
                  v-if="isErrorField('additionalInformation')"
                  color="brand.red"
                  font-size="sm"
                  my="xs"
                >
                  {{
                    $t("common.error.fieldTooLong", {
                      field: $t("productReturn.productEditor.additionalInformation"),
                    })
                  }}
                </c-box>
              </c-form-control>

              <c-form-control is-required :is-invalid="inputIsInvalid('count')" mb="2xl">
                <c-form-label for="count" font-weight="bold" mt="sm" mb="xs">
                  {{ $t("productReturn.productEditor.affectedCount") }}
                </c-form-label>

                <c-input
                  id="count"
                  size="lg"
                  border-radius="input"
                  :max-width="{ sm: '50%', md: '30%', lg: '20%' }"
                  v-model.number="form.count"
                  type="number"
                  :maxlength="maxRowCount.toString().length"
                  min="1"
                  :max="maxRowCount"
                  @change="form.count = limitNumber(form.count, 1, maxRowCount, 1)"
                  @keyup="onFieldChange('count')"
                  @blur="onFieldBlur('count')"
                  data-cy="cr-product-count"
                />
                <c-box v-if="isErrorField('count')" color="brand.red" font-size="sm" my="xs">
                  {{
                    $t("common.error.fieldInvalid", {
                      field: $t("productReturn.siteEditor.count"),
                    })
                  }}
                </c-box>
              </c-form-control>

              <c-box v-if="form.count" pt="xs" mb="2xl">
                <c-heading as="h4" font-size="md" font-weight="bold" mb="xs">
                  {{ $t("productReturn.productEditor.serialsAffected") }}
                </c-heading>
                <c-text font-size="sm" mb="xs">
                  {{ $t("productReturn.productEditor.serialsAffectedDetails") }}
                </c-text>

                <c-grid
                  :template-columns="{
                    base: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  }"
                  gap="xl"
                >
                  <c-grid-item v-for="n in limitNumber(form.count, 1, 100, 1)" :key="'RMA_PE_' + n">
                    <c-form-control mb="0" :is-invalid="serialIsInvalid(n - 1)">
                      <c-form-label :for="'RMA_PE_INPUT_' + n" class="sr-only">
                        {{ $t("productReturnV2.serialNumber") }}
                      </c-form-label>
                      <c-flex
                        position="relative"
                        align-items="center"
                        width="100%"
                        v-chakra="{
                          '.sn-is-loading ~ .loading-spinner': {
                            display: 'inherit',
                          },
                          '.sn-expired ~ .is-expired': {
                            display: 'inherit',
                          },
                          '.sn-valid ~ .is-valid': {
                            display: 'inherit',
                          },
                          '.sn-invalid ~ .not-found': {
                            display: 'inherit',
                          },
                          '.sn-mismatch ~ .is-mismatch': {
                            display: 'inherit',
                          },
                          '.sn-old-product ~ .too-old-product': {
                            display: 'inherit',
                          },
                          '.loading-spinner, .is-expired, .not-found, .is-valid, .too-old-product, .is-mismatch':
                            {
                              padding: 0,
                              display: 'none',
                              position: 'absolute',
                              top: '1rem',
                              right: '1rem',
                              cursor: 'pointer',
                              transform: 'translateY(-50%)',
                            },
                          '.is-expired, .not-found, .is-valid, .too-old-product, .is-mismatch': {
                            top: '50%',
                            transform: 'translateY(-50%)',
                          },
                          'input.sn-old-product': {
                            borderColor: 'red.300',
                            borderWidth: '2px',
                          },
                        }"
                        :data-cy="`cr-product-serial-wrapper-${n}`"
                      >
                        <c-input
                          :id="'RMA_PE_INPUT_' + n"
                          v-model.trim="form.serials[n - 1]"
                          @input="debouncedOnSerialNumberChange"
                          @blur="
                            (event) => {
                              onFieldBlur(getSerialValidationField(n - 1), true);
                              clearDebounceAndTriggerSerialNumberChange(event);
                            }
                          "
                          @keyup="onFieldChange(getSerialValidationField(n - 1), true)"
                          type="text"
                          px="4"
                          height="12"
                          font-size="1rem !important"
                          width="100%"
                          border-radius="input"
                          class="serial-input"
                          :data-cy="`cr-product-serial-${n}`"
                          bg="transparent"
                        />
                        <spinner size="sm" />
                        <c-tooltip
                          hasArrow
                          p="sm"
                          border-radius="input"
                          :label="
                            $t('productReturn.productEditor.serialNumber.expired', {
                              date: formattedWarrantyEnd(form.serials[n - 1]),
                            })
                          "
                        >
                          <c-text as="span" class="is-expired" tabindex="0" z-index="dropdown">
                            <c-icon
                              name="alert"
                              color="brand.orange"
                              size="1.4rem"
                              data-cy="is-expired-icon"
                            />
                          </c-text>
                        </c-tooltip>
                        <c-tooltip
                          hasArrow
                          p="sm"
                          border-radius="input"
                          :label="$t('productReturn.productEditor.serialNumber.notFound')"
                        >
                          <c-text as="span" class="not-found" tabindex="0" z-index="dropdown">
                            <c-icon
                              name="alert"
                              color="brand.red"
                              size="1.4rem"
                              data-cy="not-found-icon"
                            />
                          </c-text>
                        </c-tooltip>
                        <c-tooltip
                          hasArrow
                          p="sm"
                          border-radius="input"
                          :label="$t('productReturn.productEditor.serialNumber.oldProduct')"
                        >
                          <c-text as="span" class="too-old-product" tabindex="0" z-index="dropdown">
                            <c-icon
                              name="alert"
                              color="brand.red"
                              size="1.4rem"
                              data-cy="old-product-icon"
                            />
                          </c-text>
                        </c-tooltip>
                        <c-tooltip
                          hasArrow
                          p="sm"
                          border-radius="input"
                          :label="
                            $t('productReturn.productEditor.serialNumber.valid', {
                              date: formattedWarrantyEnd(form.serials[n - 1]),
                            })
                          "
                        >
                          <c-text as="span" class="is-valid" tabindex="0" z-index="dropdown">
                            <c-icon
                              name="info"
                              color="brand.green"
                              size="1.4rem"
                              data-cy="is-valid-icon"
                            />
                          </c-text>
                        </c-tooltip>
                        <c-tooltip
                          hasArrow
                          p="sm"
                          border-radius="input"
                          :label="$t('productReturn.productEditor.serialNumber.skuDoesNotMatch')"
                        >
                          <c-text as="span" class="is-mismatch" tabindex="0" z-index="dropdown">
                            <c-icon
                              name="alert"
                              color="brand.orange"
                              size="1.4rem"
                              data-cy="is-mismatch-icon"
                            />
                          </c-text>
                        </c-tooltip>
                      </c-flex>
                    </c-form-control>
                  </c-grid-item>
                </c-grid>
                <c-box v-if="blockingSerialCount > 0" color="brand.red" font-size="sm" my="xs">
                  {{ $t("productReturn.productEditor.serialNumber.oldProduct") }}
                </c-box>
                <c-box v-if="anySerialIsInvalid" color="brand.red" font-size="sm" my="xs">
                  {{ $t("productReturn.productEditor.serialNumber.someAreInvalid") }}
                </c-box>
                <c-box v-if="duplicateSerialsExist" color="brand.red" font-size="sm" my="xs">
                  {{ $t("productReturn.productEditor.serialNumber.duplicatesDetected") }}
                </c-box>
              </c-box>

              <c-box>
                <c-text as="h3" font-weight="bold" mb="xs">
                  {{ $t("productReturnV2.productEditor.returnHandling") }}
                  <c-text as="span" color="brand.greenDark">*</c-text>
                </c-text>

                <c-grid
                  grid-template-columns="auto"
                  :grid-template-areas="`
                    'first'
                    'second'
                    'third'
                    'fourth'
                  `"
                  mt="xs"
                  v-chakra="{
                    '& label': {
                      fontSize: 'sm',
                    },
                  }"
                >
                  <c-radio-group
                    @change="onOrderReplacementRadioSelect"
                    v-model="replacementOption"
                    fontSize="sm"
                    d="contents"
                    mt="sm"
                  >
                    <c-radio grid-area="first" size="sm" value="quote" tabindex="1">
                      {{ $t("productReturnV2.productEditor.quoteWanted") }}
                    </c-radio>
                    <c-radio grid-area="fourth" size="sm" value="replacement" tabindex="1">
                      {{ $t("productReturnV2.productEditor.replacementNeeded") }}
                    </c-radio>
                  </c-radio-group>
                  <c-box grid-area="second" font-size="sm" ml="lg">
                    {{ $t("productReturnV2.productEditor.pleaseSee") }}
                    <c-link
                      :href="repairServicePriceListUrl"
                      text-decoration="underline"
                      target="_blank"
                    >
                      {{ $t("productReturnV2.repairServicePriceList") }}
                    </c-link>
                    <!-- The tooltip below renders on top of the icon even if the placement is set to bottom. Only works if one zooms the page out heavily. Maybe just chakra-vue bugging. -->
                    <!-- The reason the icon was placed outside the c-radio was that on touch screens the open-by-tap didn't work from inside c-radio. -->
                    <c-tooltip
                      has-arrow
                      placement="bottom"
                      p="sm"
                      border-radius="input"
                      :label="$t('productReturnV2.repairServicePriceList.tooltip')"
                    >
                      <c-text as="span" tabindex="0">
                        <c-icon
                          name="info"
                          color="brand.blue"
                          size="1.4rem"
                          data-cy="repair-service-pirce-list-info-icon"
                        />
                      </c-text>
                    </c-tooltip>
                  </c-box>
                  <c-checkbox-group
                    v-model="optionalCheckboxes"
                    @change="onQuoteOptionalCheckBoxChange"
                    size="sm"
                    mt="2xs"
                    mb="sm"
                    grid-area="third"
                    :ml="{ md: '1.5rem' }"
                  >
                    <c-checkbox
                      :is-disabled="isQuoteOptionalCheckBoxDisabled"
                      value="requireQuote"
                      tabindex="2"
                    >
                      {{ $t("productReturnV2.productEditor.requireQuote") }}
                    </c-checkbox>
                    <c-checkbox
                      :is-disabled="isQuoteOptionalCheckBoxDisabled"
                      value="discardPermission"
                      tabindex="2"
                    >
                      {{ $t("productReturnV2.productEditor.discardPermission") }}
                    </c-checkbox>
                  </c-checkbox-group>
                </c-grid>
              </c-box>
            </c-box>
          </c-box>
        </c-modal-body>

        <c-modal-footer justifyContent="center">
          <c-button
            variation="solid"
            border-color="brand.beige"
            background="brand.beige"
            @click="submit"
            data-cy="cr-product-save"
            :disabled="isSubmitDisabled"
          >
            {{ $t("common.saveAndClose") }}
          </c-button>
        </c-modal-footer>
      </c-modal-content>
      <c-modal-overlay />
    </form>
  </c-modal>
</template>

<script lang="ts">
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import QuickProductReturnSearch from "@/components/products/QuickProductReturnSearch.vue";
import Spinner from "@/components/Spinner.vue";
import config from "@/config";
import Button from "@/design-system/elements/Button.vue";
import { getRepairServicePriceListUrl } from "@/util/PriceListUtil";
import {
  CBox,
  CButton,
  CCheckbox,
  CCheckboxGroup,
  CFlex,
  CFormControl,
  CFormLabel,
  CGrid,
  CGridItem,
  CHeading,
  CIcon,
  CInput,
  CLink,
  CModal,
  CModalBody,
  CModalCloseButton,
  CModalContent,
  CModalFooter,
  CModalHeader,
  CModalOverlay,
  CRadio,
  CRadioGroup,
  CSelect,
  CText,
  CTextarea,
  CTooltip,
} from "@chakra-ui/vue";
import {
  IMaintenanceReturnProductRequest,
  IProductSearchResultValue,
  IWarrantyInfo,
  Logger,
  IRootState,
} from "@iloq/shared";
import { debounce } from "lodash";
import moment from "moment-timezone";
import { defineComponent, PropType } from "vue";
import { mapState } from "vuex";
import { WarrantyInfoNotFoundError } from "../../WarrantyInfoNotFoundError";
import {
  productValidationWithoutProductCategory,
  productValidationWithProductCategory,
} from "./RMACRShared";
import {
  getCategoryTypes,
  getProblemOccurenceTypes,
  getReturnReasonTypes,
} from "./RMATranslationHelpers";

const maxRows = parseInt(config.get("customerReturnMaxSize"), 10) || 500;

enum ReplacementOptions {
  replacement = "replacement",
  quote = "quote",
  requireQuote = "requireQuote",
  discardPermission = "discardPermission",
}

enum SNInputState {
  valid = "sn-valid",
  expired = "sn-expired",
  mismatch = "sn-mismatch",
  invalid = "sn-invalid",
  loading = "sn-is-loading",
  oldProduct = "sn-old-product",
}

const validateWithoutProductCategory = {
  form: productValidationWithoutProductCategory,
};

export const validateWithProductCategory = {
  form: productValidationWithProductCategory,
};

interface IWarrantyInfos {
  [serialNumber: string]: string;
}

interface IForm {
  [key: string]: any;
  sku: string;
}

export default defineComponent({
  mixins: [FormValidationMixin],
  components: {
    QuickProductReturnSearch,
    Button,
    Spinner,
    CTooltip,
    CRadio,
    CRadioGroup,
    CCheckbox,
    CCheckboxGroup,
    CText,
    CModal,
    CModalBody,
    CModalCloseButton,
    CModalContent,
    CModalFooter,
    CModalHeader,
    CModalOverlay,
    CButton,
    CBox,
    CFormLabel,
    CFormControl,
    CSelect,
    CTextarea,
    CInput,
    CFlex,
    CGrid,
    CGridItem,
    CHeading,
    CIcon,
    CLink,
  },
  props: {
    showProductDialog: {
      type: Boolean,
      required: true,
    },
    proFormaRequired: {
      type: Boolean,
      required: true,
    },
    productToEdit: {
      type: Object as PropType<{ index: number; product: IMaintenanceReturnProductRequest }>,
      default: null,
    },
  },

  data() {
    return {
      debouncedOnSerialNumberChange: null,
      optionalCheckboxes: [],
      replacementOption: ReplacementOptions.quote,
      loadingSerialCount: 0,
      blockingSerialCount: 0,
      form: {
        $hasErrors: false,
        sku: "",
        name: "",
        count: "",
        returnReason: null,
        problemOccurence: null,
        additionalInformation: "",
        serials: [],
        repairPermission: true,
        replacementNeeded: false,
        discardPermission: false,
      } as IForm,
      warrantyInfos: {} as IWarrantyInfos,
      notFoundWarrantyInfos: [],
      returnReasonTypes: getReturnReasonTypes(),
      problemOccurenceTypes: getProblemOccurenceTypes(),
      categoryTypes: getCategoryTypes(),
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    isSubmitDisabled(): boolean {
      return (
        this.$v.form.serials.$invalid || this.duplicateSerialsExist || this.blockingSerialCount > 0
      );
    },
    maxRowCount(): number {
      return maxRows;
    },
    isQuoteOptionalCheckBoxDisabled(): boolean {
      return !(this.replacementOption === ReplacementOptions.quote);
    },
    anySerialIsInvalid(): boolean {
      let result = false;
      for (let i = 0; i < this.form.serials.length; i++) {
        result = result || this.$v?.form?.serials?.$each?.[i].$invalid === true;
      }
      return result;
    },
    duplicateSerialsExist(): boolean {
      return !!this.form.serials.find(
        (serial: string, index: number) =>
          serial !== "" && this.form.serials.lastIndexOf(serial) !== index,
      );
    },
    repairServicePriceListUrl(): string {
      return getRepairServicePriceListUrl(this.user);
    },
  },
  validations() {
    return this.proFormaRequired ? validateWithProductCategory : validateWithoutProductCategory;
  },
  beforeMount() {
    this.debouncedOnSerialNumberChange = debounce(this.onSerialNumberChange, 1000);

    if (this.productToEdit?.product) {
      const {
        sku,
        name,
        count,
        returnReason,
        problemOccurence,
        additionalInformation,
        serials,
        repairPermission,
        replacementNeeded,
        discardPermission,
        tariffCode,
      } = this.productToEdit.product;

      this.form = {
        $hasErrors: false,
        sku,
        name,
        count,
        returnReason,
        problemOccurence,
        additionalInformation,
        serials,
        repairPermission,
        replacementNeeded,
        discardPermission,
        tariffCode,
      };

      this.initHelperVariablesOnEdit();
    }
  },
  methods: {
    initHelperVariablesOnEdit(): void {
      if (this.form.replacementNeeded) {
        this.replacementOption = ReplacementOptions.replacement;
        return;
      }

      if (this.form.repairPermission === false) {
        this.optionalCheckboxes.push(ReplacementOptions.requireQuote);
      }

      if (this.form.discardPermission === true) {
        this.optionalCheckboxes.push(ReplacementOptions.discardPermission);
      }

      this.loadingSerialCount = 0;
      this.blockingSerialCount = 0;
    },
    limitNumber(t: any, min: number, max: number, d: number): number {
      if (t) {
        const s = `${t}`;
        const v: any = Number(s.replace(/,/g, "."));
        if (!isNaN(v)) {
          if (v < min) {
            return min;
          }
          if (v > max) {
            return max;
          }
          return v;
        }
      }
      return d;
    },
    productPickedFromQuickSearch({ product_name, subtitle, sku }: IProductSearchResultValue): void {
      this.form.name = subtitle ? `${product_name} - ${subtitle}` : product_name;
      this.form.sku = sku;
    },
    onQuoteOptionalCheckBoxChange(values: string[]): void {
      this.form.repairPermission = !values.some((val) => val === ReplacementOptions.requireQuote);
      this.form.discardPermission = values.some(
        (val) => val === ReplacementOptions.discardPermission,
      );
    },
    onOrderReplacementRadioSelect(): void {
      this.form.replacementNeeded = this.replacementOption === ReplacementOptions.replacement;
      this.form.repairPermission = this.replacementOption === ReplacementOptions.quote;
      if (this.form.replacementNeeded) {
        this.optionalCheckboxes = [];
        this.form.discardPermission = false;
      }
    },
    deleteProduct() {
      this.form.sku = "";
      this.form.name = "";
    },
    clearDebounceAndTriggerSerialNumberChange(event: any): void {
      if (this.debouncedOnSerialNumberChange) {
        this.debouncedOnSerialNumberChange.cancel();
      }
      this.onSerialNumberChange(null, event);
    },
    async getWarrantyInfoForSerialNumber(serialNumber: string): Promise<IWarrantyInfo> {
      return this.$store.dispatch("returns/getWarrantyInfo", serialNumber);
    },
    warrantyInfo(sn?: string): IWarrantyInfo | undefined {
      return sn && this.$data.warrantyInfos?.[sn];
    },
    async getWarrantyInfo(sn: string): Promise<IWarrantyInfo> {
      const warrantyInfo = this.warrantyInfo(sn);

      if (warrantyInfo) {
        return warrantyInfo;
      }

      if (this.$data.notFoundWarrantyInfos.includes(sn)) {
        throw new WarrantyInfoNotFoundError("Warranty end not found");
      }

      const newWarrantyInfo = await this.getWarrantyInfoForSerialNumber(sn);

      this.$data.warrantyInfos = {
        ...this.$data.warrantyInfos,
        [sn]: newWarrantyInfo,
      };

      return newWarrantyInfo;
    },
    formattedWarrantyEnd(serialNumber?: string): string | undefined {
      const warrantyInfo = this.warrantyInfo(serialNumber);
      return warrantyInfo && moment(warrantyInfo.warrantyEnd).format("D.M.YYYY");
    },
    clearSerialNumberFieldStyles(input: HTMLInputElement, ignoreBlockingSerialCount: boolean) {
      if (!ignoreBlockingSerialCount && input.classList.contains(SNInputState.oldProduct)) {
        this.blockingSerialCount--;
      }
      input.classList.remove(...Object.values(SNInputState));
    },
    addStateStyle(input: HTMLInputElement, state: SNInputState) {
      input.classList.add(state);
    },
    clearLoadingAndAddState(input: HTMLInputElement, state: SNInputState) {
      input.classList.remove(SNInputState.loading);
      this.addStateStyle(input, state);
    },
    async onSerialNumberChange(
      newValue: any,
      { target: input }: { target: HTMLInputElement },
      fakeChange = false,
    ) {
      try {
        if (!this.$store?.state?.features?.warrantyinfo) {
          return;
        }

        const serialNumber = input?.value?.trim();
        const serialNumberValidationFailed = input?.getAttribute("aria-invalid") === "true";

        this.clearSerialNumberFieldStyles(input, fakeChange);

        if (!serialNumber || serialNumberValidationFailed) {
          return;
        }

        this.addStateStyle(input, SNInputState.loading);
        this.loadingSerialCount++;

        const { warrantyEnd, SKU } = await this.getWarrantyInfo(serialNumber);

        if (moment().isBefore(moment(warrantyEnd))) {
          this.clearLoadingAndAddState(input, SNInputState.valid);
        } else if (moment().subtract(3, "y").isAfter(moment(warrantyEnd))) {
          this.clearLoadingAndAddState(input, SNInputState.oldProduct);
          this.blockingSerialCount++;
        } else {
          this.clearLoadingAndAddState(input, SNInputState.expired);
        }
        if (SKU && SKU !== this.form.sku) {
          this.clearSerialNumberFieldStyles(input, fakeChange);
          this.addStateStyle(input, SNInputState.mismatch);
        }
        this.loadingSerialCount--;
      } catch (err) {
        if (!(err instanceof WarrantyInfoNotFoundError)) {
          Logger.error(err);
        }
        this.$data.notFoundWarrantyInfos = [...this.$data.notFoundWarrantyInfos, input?.value];
        this.clearLoadingAndAddState(input, SNInputState.invalid);
        this.loadingSerialCount--;
      }
    },
    hasErrors(): boolean {
      return this.form.$hasErrors;
    },
    onChanged(closed: boolean) {
      this.form.$isClosed = closed;
    },
    cancel() {
      this.$emit("hide");
    },
    submit() {
      setTimeout(() => {
        if (
          this.duplicateSerialsExist ||
          this.blockingSerialCount > 0 ||
          this.loadingSerialCount > 0
        ) {
          return;
        }

        this.$v.$touch();

        if (!this.$v.$invalid) {
          if (this.productToEdit) {
            this.$emit("editReturnProduct", this.form, this.productToEdit.index);
          } else {
            this.$emit("addReturnProduct", this.form);
          }
        }
      }, 100);
    },
    inputIsInvalid(field: any): boolean {
      return this.getFieldClasses(field)?.["is-invalid"] === true;
    },
    getSerialValidationField(index: number): any {
      return this.$v?.form?.serials?.$each?.[index];
    },
    serialIsInvalid(index: number): boolean {
      return !!this.$v?.form?.serials?.$each?.[index]?.$invalid;
    },
  },
  watch: {
    "form.count": function () {
      if (this.form && this.form.count) {
        if (!this.form.serials) {
          this.form.serials = [];
        }
        const c: any = this.limitNumber(this.form.count, 1, maxRows, 1);
        this.form.serials = this.form.serials.slice(0, c);

        this.$nextTick(() => {
          this.$nextTick(() => {
            this.blockingSerialCount = 0;
            document.querySelectorAll("input.serial-input").forEach((element) => {
              this.onSerialNumberChange(
                null,
                { target: element } as { target: HTMLInputElement },
                true,
              );
            });
          });
        });
      }
    },
    isError: function () {
      this.form.$hasErrors = this.isError;
    },
  },
});
</script>
